/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { IProductDimensionsWithAvailabilitiesFull } from '@msdyn365-commerce/commerce-entities';
import { CacheType, createObservableDataAction, IAction, IActionContext, IActionInput, IAny, ICreateActionContext, IGeneric } from '@msdyn365-commerce/core';
import { BypassCache, ProductDimension, ProductDimensionAvailabilitySearchCriteria, ProductsDataActions, SimpleProduct } from '@msdyn365-commerce/retail-proxy';

import { ArrayExtensions } from './utilities';
import { generateProductImageUrl, getDimensionValuesFromQuery, getSelectedProductIdFromActionInput } from './utilities/utils';

/**
 * Stores data about the selected product.
 */
export interface ISelectedProduct extends SimpleProduct {
    productVariant?: SimpleProduct;
}

/**
 * Get selected variant action input class.
 */
export class SelectedVariantInput implements IActionInput {
    public productId: number;

    public channelId: number;

    public matchingDimensionValues?: ProductDimension[];

    public bypassCache?: BypassCache;

    public constructor(productId: number, channelId: number, matchingDimensionValues?: ProductDimension[], bypassCache?: BypassCache) {
        this.productId = productId;
        this.channelId = channelId;
        this.matchingDimensionValues = matchingDimensionValues;
        this.bypassCache = bypassCache;
    }

    /**
     * Cache key.
     * @returns String.
     */
    public getCacheKey: () => string = () => 'SelectedVariant';

    /**
     * Cache object type.
     * @returns String.
     */
    public getCacheObjectType: () => string = () => 'SimpleProduct';

    /**
     * Cache type.
     * @returns String.
     */
    public dataCacheType = (): CacheType => 'none';
}

/**
 * CreateInput method for the getSelectedVariant data action.
 * @param inputData - The input data passed to the createInput method.
 * @returns The input for the ata action getSelectedVariantNewAction.
 */
const createInput = (inputData: ICreateActionContext<IGeneric<IAny>>): SelectedVariantInput => {
    const productId = getSelectedProductIdFromActionInput(inputData);

    if (productId) {
        return new SelectedVariantInput(+productId, +inputData.requestContext.apiSettings.channelId);
    }
    throw new Error('Unable to create SelectedVariantInput, no productId found on module config or query');

};

/**
 * Checks if given product has any unmatched dimensions.
 * Modifies product entity to use matched dimension values.
 * @param product - The product to analyze and modify.
 * @param matchingDimensionValues - Matched dimensions.
 * @returns True if has any unmatched dimension, false if all dimensions are specified.
 */
const checkIfHasUnmatchedDimensions = (product: SimpleProduct, matchingDimensionValues: ProductDimension[]): boolean => {
    let hasUnmatchedDimension: boolean = false;
    if (product.Dimensions) {
        product.Dimensions = product.Dimensions.map(dimension => {
            return { ...dimension };
        });
        for (const dimension of product.Dimensions) {
            const matchedTargetDimension = matchingDimensionValues.find(
                targetDimension => targetDimension.DimensionTypeValue === dimension.DimensionTypeValue);

            if (matchedTargetDimension) {
                dimension.DimensionValue = matchedTargetDimension.DimensionValue;
            } else {
                hasUnmatchedDimension = true;
            }
        }
    }
    return hasUnmatchedDimension;
};

/**
 * Retrieves product dimensions with availabilities.
 * @param product - Product for which the dimensions need to be retrieved.
 * @param matchingDimensionValues - Selected dimensions - filter criteria for the dimensions API.
 * @param context - Action context.
 * @returns Array of dimensions.
 */
const getFullDimensions = async (product: SimpleProduct, matchingDimensionValues: ProductDimension[], context: IActionContext) => {
    let fullDimensions: IProductDimensionsWithAvailabilitiesFull[] = [];
    const fullDimensionPromises = (product.Dimensions ?? []).map(dimension => {
        const searchCriteria: ProductDimensionAvailabilitySearchCriteria =
        {
            RequestedDimensionTypeValue: dimension.DimensionTypeValue,
            MatchingDimensionValues: matchingDimensionValues,
            DefaultWarehouseOnly: false,
            FilterByChannelFulfillmentGroup: true,
            DeliveryModeTypeFilterValue: 0
        };
        const dimensionValuesPromise = ProductsDataActions.getDimensionValuesWithEstimatedAvailabilitiesAsync(
            { callerContext: context, queryResultSettings: {} },
            product.MasterProductId ? product.MasterProductId : product.RecordId,
            searchCriteria
        );

        return dimensionValuesPromise.then(dimensionValues => {
            const fullDimension: IProductDimensionsWithAvailabilitiesFull = {
                ...dimension,
                dimensionValuesWithInventory: dimensionValues
            };
            return fullDimension;
        });
    });
    fullDimensions = await Promise.all(fullDimensionPromises);
    return fullDimensions;
};

/**
 * Retrieves product variant for the given input or undefined if not found.
 * @param input - Action input.
 * @param context - Action context.
 * @param fullDimensions - Product dimensions with availabilities.
 * @returns A product variant that is available or undefined.
 */
const getProductVariant = async (
    input: SelectedVariantInput,
    context: IActionContext,
    fullDimensions: IProductDimensionsWithAvailabilitiesFull[]) => {

    let productVariant: SimpleProduct | undefined;
    if (ArrayExtensions.hasElements(fullDimensions) &&
        ArrayExtensions.hasElements(fullDimensions[0].dimensionValuesWithInventory) &&
        ArrayExtensions.hasElements(fullDimensions[0].dimensionValuesWithInventory[0].ProductIds)) {
        const variantProductId = fullDimensions[0].dimensionValuesWithInventory[0].ProductIds[0];
        productVariant = await ProductsDataActions.getByIdAsync(
            { callerContext: context, bypassCache: input.bypassCache }, variantProductId, input.channelId);
        productVariant = { ...productVariant };
        const variantImageUrl = generateProductImageUrl(productVariant, context.requestContext.apiSettings);

        if (variantImageUrl) {
            productVariant.PrimaryImageUrl = variantImageUrl;
        }
    }

    return productVariant;
};

/**
 * Action method for the getSelectedVariant data action.
 * @param input - The action input class.
 * @param context - The action context.
 * @returns Simple product.
 */
async function getSelectedVariantAction(input: SelectedVariantInput, context: IActionContext): Promise<ISelectedProduct | null> {
    const matchingDimensionValues = input.matchingDimensionValues ?? getDimensionValuesFromQuery(context.requestContext.url.requestUrl);

    let product = await ProductsDataActions.getByIdAsync(
        { callerContext: context, bypassCache: input.bypassCache }, input.productId, input.channelId);

    // Need to dereference this before editing it. Otherwise we might not
    // properly get the mobx events because if things aren't properly observable
    // they won't fire when you set them, and then if you don't deref the value in
    // the cache will match the value when you try to save it, so it won't detect any
    // changes there either
    product = { ...product };
    const hasUnmatchedDimension = checkIfHasUnmatchedDimensions(product, matchingDimensionValues);

    const newImageUrl = generateProductImageUrl(product, context.requestContext.apiSettings);

    if (newImageUrl) {
        product.PrimaryImageUrl = newImageUrl;
    }

    const fullDimensions: IProductDimensionsWithAvailabilitiesFull[] = await getFullDimensions(product, matchingDimensionValues, context);

    // Retrieve product variants only if matching dimensions are provided.
    const productVariant: SimpleProduct | undefined = ArrayExtensions.hasElements(matchingDimensionValues) ? await
    getProductVariant(input, context, fullDimensions) : undefined;

    // Update dimension value back to the one that comes from API since the URL query might have a different format for the dimension values.
    if (productVariant?.Dimensions && product.Dimensions) {
        for (const dimension of product.Dimensions) {
            const matchedTargetDimensionFromApi = productVariant.Dimensions.find(
                targetDimension => targetDimension.DimensionTypeValue === dimension.DimensionTypeValue);
            const matchedTargetDimensionFromSearch = matchingDimensionValues.find(
                targetDimension => targetDimension.DimensionTypeValue === dimension.DimensionTypeValue);

            if (matchedTargetDimensionFromSearch && matchedTargetDimensionFromApi) {
                dimension.DimensionValue = matchedTargetDimensionFromApi.DimensionValue;
            }
        }
    }

    if (!hasUnmatchedDimension && ArrayExtensions.hasElements(matchingDimensionValues) && productVariant) {
        return productVariant;
    }

    const selectedProduct: ISelectedProduct = product;
    selectedProduct.productVariant = productVariant;
    return selectedProduct;
}

/**
 * The complete getSelectedVariant data action.
 */
export const getSelectedVariantActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-selected-variant',
    action: getSelectedVariantAction as IAction<ISelectedProduct | null>,
    input: createInput
});

export default getSelectedVariantActionDataAction;
